import { format } from "date-fns/format";
import { parse } from "date-fns/parse";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  ColumnOneTableCellSkeleton,
  SecondaryButton,
  Table,
  TableCellSkeleton,
} from "../../../components";
import { columnHelper } from "../../../components/shared/SharedSessionColumns";
import { useModalContext } from "../../../contexts/ModalContext";
import { usePlayerContext } from "../../../contexts/PlayerContext";
import { useUIContext } from "../../../contexts/UIContext";
import { PlayerForm } from "../../../features/Player/PlayerForm";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { playerSlice } from "../../../redux/slices/playerSlice";
import { useGetPlayerByIdQuery } from "../../../services";
import { emptyPlayerFormValues } from "../../../shared/Constants";
import {
  disableEditPlayer,
  joinClassNames,
  showPlayerHandedness,
  upsertPlayerById,
} from "../../../shared/Functions";
import { Player, PlayerFormValues } from "../../../types";

interface RosterProps {
  fetchingPlayers: boolean;
  storedPlayerList: Player[];
  fetchError?: string | null;
}

export const Roster: React.FC<RosterProps> = ({
  fetchingPlayers,
  storedPlayerList,
  fetchError,
}): JSX.Element => {
  // State and AppContext
  const [selectedPageSize, setSelectedPageSize] = useState(
    sessionStorage.getItem("playersPageSize") || "10",
  );
  const { setModalState } = useModalContext();
  const { setDisplayBack } = useUIContext();
  const { playerIdToRetrieve, setPlayerIdToRetrieve } = usePlayerContext();

  // Redux calls
  const orgType = useAppSelector((state) => state.org).type;

  // RTK slice actions
  const dispatch = useAppDispatch();
  const { storePlayers } = playerSlice.actions;

  // RTK Queries
  const { data: singlePlayer } = useGetPlayerByIdQuery(playerIdToRetrieve, {
    skip: !playerIdToRetrieve,
  });

  const processedPlayerRef = useRef<string>("");

  useEffect(() => {
    if (
      singlePlayer &&
      playerIdToRetrieve &&
      processedPlayerRef.current !== playerIdToRetrieve
    ) {
      const updatedPlayerList = upsertPlayerById(
        storedPlayerList,
        singlePlayer,
      );
      dispatch(storePlayers(updatedPlayerList));
      setPlayerIdToRetrieve("");
      processedPlayerRef.current = playerIdToRetrieve;
    }
  }, [
    singlePlayer,
    playerIdToRetrieve,
    storedPlayerList,
    dispatch,
    storePlayers,
    setPlayerIdToRetrieve,
  ]);

  const playersSortedByLastName = useMemo(
    () =>
      [...storedPlayerList].sort((a, b) => (a.lastName < b.lastName ? -1 : 1)),
    [storedPlayerList],
  );

  const playersColumns = useMemo(
    () => [
      columnHelper.accessor("playersTableValues.playerName", {
        header: "Player",
        size: 300,
        sortingFn: (rowA: any, rowB: any) => {
          const lastNameA = rowA.original.lastName.toLowerCase();
          const lastNameB = rowB.original.lastName.toLowerCase();
          if (lastNameA < lastNameB) return -1;
          if (lastNameA > lastNameB) return 1;
          return 0;
        },
        cell: function playerNameCell(element: any) {
          return (
            <Link
              to={`/player/${element.row.original.orgPlayerId}`}
              className="font-bold text-primary-700 hover:underline"
              data-testid="player-link"
            >
              {`${element.row.original.lastName}, ${element.row.original.firstName}`}
            </Link>
          );
        },
      }),
      columnHelper.accessor("throws", {
        header: "Throws",
        size: 200,
        cell: function throwsCell(element: any) {
          return (
            <div
              className={joinClassNames(
                !element.getValue() ? "text-red-500 italic" : "text-gray-900",
                "font-medium",
              )}
            >
              {element.getValue() ? element.getValue() : "Unknown"}
            </div>
          );
        },
      }),
      columnHelper.accessor("hits", {
        header: "Hits",
        size: 200,
        cell: function hitsCell(element: any) {
          return (
            <div
              className={joinClassNames(
                !element.getValue() ? "text-red-500 italic" : "text-gray-900",
                "font-medium",
              )}
            >
              {element.getValue() ? element.getValue() : "Unknown"}
            </div>
          );
        },
      }),
      columnHelper.accessor("playersTableValues.height", {
        header: "Height",
        enableSorting: false,
        size: 200,
        cell: function heightCell(element: any) {
          return (
            <div
              className={joinClassNames(
                !element.getValue() ? "text-red-500 italic" : "text-gray-900",
                "font-medium",
              )}
            >
              {element.getValue() ? element.getValue() : "Unknown"}
            </div>
          );
        },
      }),
      columnHelper.accessor("playersTableValues.weight", {
        header: "Weight",
        enableSorting: false,
        size: 200,
        cell: function weightCell(element: any) {
          return (
            <div
              className={joinClassNames(
                !element.getValue() ? "text-red-500 italic" : "text-gray-900",
                "font-medium",
              )}
            >
              {element.getValue() ? element.getValue() : "Unknown"}
            </div>
          );
        },
      }),
      columnHelper.accessor("dateOfBirth", {
        header: "Birth Date",
        enableSorting: false,
        size: 200,
        cell: function birthDateCell(element: any) {
          return (
            <div
              className={joinClassNames(
                element.getValue() === "1/1/1900" ||
                  element.getValue() === "Unknown"
                  ? "text-red-500 italic"
                  : "text-gray-900",
                "font-medium",
              )}
            >
              {element.getValue()}
            </div>
          );
        },
      }),
      columnHelper.accessor("editPlayer", {
        header: "",
        enableSorting: false,
        cell: function editCell(element: any) {
          const playerName = `${element.cell.row.original.firstName} ${element.cell.row.original.lastName}`;
          const orgPlayerId = element.cell.row.original.orgPlayerId;
          const playerBirthDate =
            element.row.original.dateOfBirth !== "Unknown"
              ? format(
                  parse(
                    element.row.original.dateOfBirth,
                    "M/d/yyyy",
                    new Date(),
                  ),
                  "yyyy-MM-dd",
                )
              : "1900-01-01";
          return (
            <div>
              <SecondaryButton
                className="mx-14"
                onClick={() => {
                  const playerForm: PlayerFormValues = {
                    firstName: element.row.original.firstName,
                    middleName: element.row.original.middleName,
                    lastName: element.row.original.lastName,
                    dateOfBirth: new Date(`${playerBirthDate}T00:00-0800`),
                    throws: {
                      value: element.row.original.throws,
                      label: element.row.original.throws,
                    },
                    hits: {
                      value: element.row.original.hits,
                      label: element.row.original.hits,
                    },
                    heightFeet: element.row.original.height.feet,
                    heightInches: element.row.original.height.inches,
                    weight: element.row.original.weight,
                    officialLeagueId: element.row.original.officialLeagueId,
                    orgPlayerId: element.row.original.orgPlayerId,
                  };
                  openModal(`Edit - ${playerName}`, playerForm, orgPlayerId);
                }}
                data-testid="edit-player-button"
              >
                Edit
              </SecondaryButton>
            </div>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const columnsWithoutHandOrEdit = useMemo(
    () =>
      playersColumns.filter(
        (column) =>
          column.accessorKey !== "throws" &&
          column.accessorKey !== "hits" &&
          column.accessorKey !== "editPlayer",
      ),
    [playersColumns],
  );

  const tableData = useMemo(
    () => (fetchingPlayers ? Array(10).fill({}) : playersSortedByLastName),
    [fetchingPlayers, playersSortedByLastName],
  );

  const tableColumns = useMemo(
    () =>
      fetchingPlayers
        ? (!showPlayerHandedness(orgType) && disableEditPlayer(orgType)
            ? columnsWithoutHandOrEdit
            : playersColumns
          ).map((column, index) => ({
            ...column,
            cell:
              index === 0 ? (
                <ColumnOneTableCellSkeleton />
              ) : (
                <TableCellSkeleton />
              ),
          }))
        : !showPlayerHandedness(orgType) && disableEditPlayer(orgType)
          ? columnsWithoutHandOrEdit
          : playersColumns,
    [fetchingPlayers, columnsWithoutHandOrEdit, orgType, playersColumns],
  );

  const openModal = (
    title: string,
    playerFormValues?: PlayerFormValues,
    orgPlayerId?: string,
  ) => {
    setModalState((prev) => ({
      ...prev,
      shouldOpen: true,
      title: title,
      singleStep: (
        <PlayerForm
          formValues={
            playerFormValues ? playerFormValues : emptyPlayerFormValues
          }
          currentOrgPlayerId={orgPlayerId ? orgPlayerId : ""}
        />
      ),
    }));
  };

  const buttonAction = () => {
    openModal("Add New Player");
  };

  useEffect(() => {
    setDisplayBack && setDisplayBack(false);
    if (selectedPageSize) {
      sessionStorage.setItem("playersPageSize", selectedPageSize);
    }
  }, [setDisplayBack, selectedPageSize]);

  return (
    <main className="relative pb-20 z-0 overflow-y-auto w-full">
      <div data-testid="players-table-container">
        <Table
          columns={tableColumns}
          data={tableData}
          data-testid="players-table"
          selectedPageSize={selectedPageSize}
          setSelectedPageSize={setSelectedPageSize}
          displayFooter={true}
          fetching={fetchingPlayers}
          fetchError={fetchError}
          customRowClassName="players-table-row"
          component="players"
          buttons={[
            {
              type: "primary",
              action: buttonAction,
              text: "Add Player",
              dataTestid: "add-player-button",
            },
          ]}
        />
      </div>
    </main>
  );
};
